import React from 'react'
import {
  createStyles,
  withStyles,
  Dialog,
  DialogTitle,
  ConfirmationDialog,
  CalculatorIcon,
  BasketIcon,
  Button
} from '@paintscout/ui'
import type { DialogProps, StyleClasses, MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import type { UpdateableAreaSubstrate, EditableRate } from '@paintscout/util/builder'
import {
  getEditableRateUpdateableProducts,
  getObjectLabels,
  updateAreaSubstrate,
  getAreaSubstrate,
  getArea,
  getFeature
} from '@paintscout/util/builder'
import type { QuoteDocument } from 'paintscout'
import DescriptionIcon from '@material-ui/icons/Description'
import SettingsIcon from '@material-ui/icons/Settings'
import { isLooselyEqual } from '@paintscout/util'
import { useQuote } from '../../context'
import RateSelectDialog from '../RateSelectDialog'
import EditRateForm from './EditRateForm'

export interface EditAreaSubstrateProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  quote: QuoteDocument
  areaKey: string
  substrateKey: string
  onConfirm: (quote: QuoteDocument, itemKey?: string) => any
  onCancel: () => any
  nextItemKey: string
  previousItemKey: string
}

export const styles = (_theme: Theme) => {
  return createStyles({
    changeButton: {
      padding: 0
    }
  })
}

function EditAreaSubstrate(props: EditAreaSubstrateProps) {
  const { quote, areaKey, substrateKey, onCancel, onConfirm, classes, nextItemKey, previousItemKey, ...dialogProps } =
    props

  const { openDialog, dismissDialog } = useDialogs()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [updatedQuote, setUpdatedQuote] = React.useState(quote)
  const { isEditable } = useQuote()
  const advancedOptionsFeature = getFeature({ options, path: 'quotes.areaSubstrates.advancedOptions' })
  const disabled = (quote.type === 'quoteTemplate' && quote?.smartTemplate) || !isEditable

  function handleCancel() {
    if (!isLooselyEqual(updatedQuote, quote)) {
      openDialog(ConfirmationDialog, {
        message: `Any unsaved changes to this ${objectLabels.substrate.value.toLowerCase()} will be lost`,
        onConfirm: () => {
          dismissDialog()
          onCancel()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      onCancel()
    }
  }

  function handleChangeSelection(_event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) {
    const area = getArea({ quote: updatedQuote, options, key: areaKey })
    const areaSubstrate = getAreaSubstrate({
      quote: updatedQuote,
      options,
      areaKey,
      key: substrateKey
    }) as UpdateableAreaSubstrate

    openDialog(RateSelectDialog, {
      quoteType: area.quoteType,
      quote: updatedQuote,
      onConfirm: (rate: EditableRate, fields?: any) => {
        // Get new values from new rate, old values or 0 as fallbacks
        const coats = rate.defaultApplications
        const prep = rate?.prep ?? areaSubstrate?.prep ?? 0
        const rateValue = {
          ...areaSubstrate?.rateValue,
          default: rate.values[coats - 1]
        }
        const name = rate?.label ?? areaSubstrate?.rate?.label ?? ''
        const clientLabel = rate?.clientLabel ?? areaSubstrate?.clientLabel ?? ''
        const notes = rate?.notes ?? areaSubstrate?.notes ?? ''
        const hours = { ...areaSubstrate?.hours }
        const price = {
          ...areaSubstrate?.price
        }
        if (fields && fields.hours) {
          hours.useCustom = true
          hours.custom = fields.hours
        }
        if (fields && fields.price) {
          price.useCustom = true
          price.custom = fields.price
        }
        const quantity = fields && fields.quantity ? fields.quantity : areaSubstrate?.quantity
        const defaultProducts = getEditableRateUpdateableProducts({ options, rate })

        const updatedAreaSubstrate: UpdateableAreaSubstrate = {
          ...areaSubstrate,
          name,
          prep,
          notes,
          coats,
          rateValue,
          rate,
          clientLabel,
          quantity,
          hours,
          products: defaultProducts,
          price
        }

        setUpdatedQuote(
          updateAreaSubstrate({ quote, options, areaKey, areaSubstrate: updatedAreaSubstrate, calculate: true })
        )
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const toggles = [
    { key: 'pricing', label: 'Hours/Cost', value: true, icon: CalculatorIcon },
    { key: 'materials', label: 'Materials', value: true, icon: BasketIcon },
    { key: 'notes', label: 'Notes', value: true, icon: DescriptionIcon }
  ]
  if (advancedOptionsFeature.enabled) {
    toggles.push({ key: 'advanced', label: 'Advanced', value: true, icon: SettingsIcon })
  }
  const checkParentHasChanges = () => !isLooselyEqual(updatedQuote, quote)

  return (
    <Dialog maxWidth={'md'} fullWidth={true} {...dialogProps}>
      <DialogTitle
        rightContent={
          !disabled && (
            <Button classes={{ root: classes.changeButton }} variant={'text'} onClick={handleChangeSelection}>
              Change Selection
            </Button>
          )
        }
      >
        {`Edit ${objectLabels.substrate.value}`}
      </DialogTitle>
      <EditRateForm
        key={substrateKey}
        options={options}
        quote={updatedQuote}
        disabled={disabled}
        areaKey={areaKey}
        editingKey={substrateKey}
        checkParentHasChanges={checkParentHasChanges}
        onChange={(areaKey, areaSubstrate) => {
          setUpdatedQuote(
            updateAreaSubstrate({ quote: updatedQuote, options, areaKey, areaSubstrate, calculate: true })
          )
        }}
        handleNavArrowClick={(itemKey) => {
          onConfirm?.(updatedQuote, itemKey)
        }}
        nextItemKey={nextItemKey}
        previousItemKey={previousItemKey}
        onConfirm={(quote, itemKey) => {
          onConfirm(quote, itemKey)
        }}
        onCancel={handleCancel}
      />
    </Dialog>
  )
}

export default withStyles(styles)(EditAreaSubstrate)
